import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IStore } from 'redux/IStore';
import { reduxForm, getFormSyncErrors, Field } from 'redux-form';
import { Button, ErrorSummary, Checkout, WizzardSteps, LoadingOverlayWrapper, CartContainer } from 'components';
import { PortalPageSection, ButtonsSection, PageTitle } from 'components/PortalStructure';
import { preventRouterLeave } from 'helpers/hooks';
import { InjectedRouter } from 'react-router';
import { getDefaultShippingAddress } from 'modules/cart';
import { ICartObject, ICart, ICartAction } from 'models/cart/cart';
import { PortalSwitchField } from 'components/FormControls';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';
import { getCountryDomainValues, getCountryCallingCodeDomainValues } from 'redux/modules/domainvalues';
import { IDomainValue, ICountryCallingCodeDomainValue } from 'models/common';
import { IUserPreferences } from 'models/user/userpreferences';
import { OrderAddressForm } from 'containers/Common/AddressForms/OrderAddressForm';
const { connect } = require('react-redux');

require('./style.css');

interface IProps {
	userPreferences?: IUserPreferences;
	currentFormValues: ICartObject;
	initialValues: ICartObject;
	cart?: ICart;
	countryDomainValues: IDomainValue[];
	location?: any;
	router: InjectedRouter;
	route: any;
	countryCallingCodeDomainValues: ICountryCallingCodeDomainValue[];

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string) => string;
	nextPage?: () => void;
	getCountryDomainValues?: () => IDomainValuesAction;
	getDefaultShippingAddress?: () => ICartAction;
	change: (field: string, value: any) => void;
	getCountryCallingCodeDomainValues?: () => IDomainValuesAction;
	goToShoppingBagStep: () => ICartAction;
	goToShop: (id: string, target?: string) => void;
	goToProduct: (id: string, target?: string) => void;
}

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CheckoutAddressForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.onUseMyDefaultShippingAddressChange = this.onUseMyDefaultShippingAddressChange.bind(this);
	}

	public componentDidMount() {
		if (this.props.userPreferences) {
			this.props.getDefaultShippingAddress();
		}

		this.props.getCountryDomainValues();
		this.props.getCountryCallingCodeDomainValues();
	}

	public onSubmit() {
		this.props.nextPage();
	}

	private onUseMyDefaultShippingAddressChange(e: any) {
		this.props.change('useDefaultShippingAddress', e.target.checked);
		if (this.props.cart.defaultShippingAddress && e.target.checked) {
			this.props.change('order.shippingName', this.props.cart.defaultShippingAddress.name);
			this.props.change('order.shippingAddressLine', this.props.cart.defaultShippingAddress.addressLine1);
			this.props.change('order.shippingZIPCode', this.props.cart.defaultShippingAddress.zipCode);
			this.props.change('order.shippingCity', this.props.cart.defaultShippingAddress.city);
			this.props.change('order.shippingCountryKey', this.props.cart.defaultShippingAddress.countryKey);
			this.props.change('order.shippingPhoneNumber', this.props.cart.defaultShippingAddress.phoneNumber);
			this.props.change('order.shippingNote', this.props.cart.defaultShippingAddress.note);
			this.props.change('order.shippingCallingCodeKey', this.props.cart.defaultShippingAddress.callingCodeKey);
		} else {
			this.props.change('order.shippingName', null);
			this.props.change('order.shippingAddressLine', null);
			this.props.change('order.shippingZIPCode', null);
			this.props.change('order.shippingCity', null);
			this.props.change('order.shippingCountryKey', null);
			this.props.change('order.shippingPhoneNumber', null);
			this.props.change('order.shippingNote', null);
			this.props.change('order.shippingCallingCodeKey', null);
		}
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'shippingName':
				return t('Cart_CheckoutAddress_Label_NameAndSurname');

			case 'shippingAddressLine':
				return t('Cart_CheckoutAddress_Label_StreetAndNumber');

			case 'shippingZIPCode':
				return t('Cart_CheckoutAddress_Label_PostCode');

			case 'shippingCity':
				return t('Cart_CheckoutAddress_Label_City');

			case 'shippingCountryKey':
				return t('Cart_CheckoutAddress_Label_Country');

			case 'email':
				return t('Cart_CheckoutAddress_Label_Email');

			case 'shippingPhoneNumber':
				return t('Cart_CheckoutAddress_Label_PhoneNumber');

			case 'shippingNote':
				return t('Cart_CheckoutAddress_Placeholder_Note');

			default:
				return key;
		}
	}

	public render() {
		const {
			handleSubmit,
			formErrors,
			submitFailed,
			t,
			location,
			countryDomainValues,
			cart,
			userPreferences,
			countryCallingCodeDomainValues,
		} = this.props;
		const { isFetching } = this.props.cart;
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.cart ? this.props.cart.error : false,
			message: this.props.cart ? this.props.cart.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
			customFormErrorsParseFunc: () => {
				const errors = [];
				Object.keys(formErrors).forEach((key) => {
					Object.keys(formErrors[key]).forEach((innerKey) => {
						if (formErrors[key][innerKey]) {
							errors.push({ error: self.mapErrorKeyToMessage(innerKey, t) + ': ' + formErrors[key][innerKey] });
						}
					});
				});
				return errors;
			},
		};

		return (
			<div className="cartCheckoutAddress">
				<PageTitle title={t('Cart_CheckoutAddress_Label_Checkout')} />
				<CartContainer>
					<div className="col-md-4">
						<Checkout
							items={cart.shoppingBasket.items}
							total={cart.shoppingBasket.total}
							goToShoppingBagStep={this.props.goToShoppingBagStep}
							itemsTotal={cart.shoppingBasket.itemsTotal}
							shippingPrice={cart.shoppingBasket.shippingPrice}
							currencyKey={cart.shoppingBasket.currencyKey}
							goToShop={this.props.goToShop}
							goToProduct={this.props.goToProduct}
						/>
					</div>
					<div className="col-md-8">
						<div className="checkoutAddressForm">
							<LoadingOverlayWrapper
								active={isFetching}
								spinner={true}
								text={t('BSPortal_Common_Loading')}
								background="rgba(0, 0, 0, 0.1)"
							>
								<div className="profileError">
									<ErrorSummary {...errorSummaryProps} />
								</div>
								<form onSubmit={handleSubmit(this.onSubmit)}>
									<PortalPageSection title={t('Cart_CheckoutAddress_Label_ShippingAddress')}>
										{userPreferences && cart.defaultShippingAddress ? (
											<div className="row">
												<div className="col-md-12">
													<Field
														name="useDefaultShippingAddress"
														component={PortalSwitchField}
														type="checkbox"
														label={t('Cart_CheckoutAddress_Label_UseDefaultShippingAddress')}
														onChangeAction={this.onUseMyDefaultShippingAddressChange}
													/>
												</div>
											</div>
										) : null}
										<OrderAddressForm
											keyPrefix="order."
											t={t}
											countryCallingCodeDomainValues={countryCallingCodeDomainValues}
											countryDomainValues={countryDomainValues}
										/>
									</PortalPageSection>
									<ButtonsSection>
										<div className="row">
											<div className="col-sm-6">
												<div className="floatLeft">
													<WizzardSteps currentStep={1} numberOfSteps={3} />
													<div className="clear" />
												</div>
												<div className="clear" />
											</div>
											<div className="col-sm-6">
												<div className="floatRight">
													<Button type="submit" disabled={isFetching}>
														{t('BSPortal_Common_Continue')}
													</Button>
													<div className="clear" />
												</div>
												<div className="clear" />
											</div>
										</div>
									</ButtonsSection>
								</form>
							</LoadingOverlayWrapper>
						</div>
					</div>
				</CartContainer>
			</div>
		);
	}
}

let CheckoutAddress = reduxForm({
	form: c.FORM_NAME_CART,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(CheckoutAddressForm);

CheckoutAddress = connect(
	(state: IStore) => {
		let currentFormValues: ICartObject = null;
		if (state.form[c.FORM_NAME_CART] && state.form[c.FORM_NAME_CART].values) {
			currentFormValues = state.form[c.FORM_NAME_CART].values;
		} else {
			currentFormValues = state.cart.cart;
		}

		return {
			initialValues: state.cart.cart,
			cart: state.cart,
			currentFormValues,
			countryDomainValues: state.domainValues.countries,
			countryCallingCodeDomainValues: state.domainValues.countryCallingCodes,
			formErrors: getFormSyncErrors(c.FORM_NAME_CART)(state),
		};
	},
	(dispatch) => ({
		getCountryDomainValues: () => dispatch(getCountryDomainValues()),
		getDefaultShippingAddress: () => dispatch(getDefaultShippingAddress()),
		getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
	}),
)(CheckoutAddress);

export { CheckoutAddress };
